import { type DocumentNode, InMemoryCache } from '@apollo/client'

import { DEFAULT_CATEGORY } from '@/constants/category'
import { getSrpUrlCacheKey } from '@/domain/srp/getSrpUrlCacheKey'
import {
  type SearchResultsByUrlInput,
  GetAuthModalStateDocument,
  GetHomeFeedAdsDocument,
  GetSearchCategoryDocument,
  GetSearchKeywordDocument,
  GetSearchLoadingStateDocument,
} from '@/generated'

import { type RequestHeaders } from './apolloClient'
import { getInitialCookieCacheData } from './cacheFromCookies'
import { currentSrpUrl } from './currentSrpUrl'
import { searchLocationVar } from './locationCache'

/**
 * HOW TO ADD A NEW CACHE VARIABLE:
 * - Create a file as: "<variable_name>Cache.ts"
 * - Add the cached variable to the "APOLLO_CACHE_VARIABLES"
 */
const APOLLO_CACHE_VARIABLES = {
  searchLocation: searchLocationVar(),
  currentSrpUrl: currentSrpUrl(),
}

export const getCacheFields = () => {
  const cache = {
    fields: {
      searchResultsPageByUrl: {
        keyArgs: (args: Record<'input', SearchResultsByUrlInput> | null) => {
          return (args && getSrpUrlCacheKey(args.input.url)) || undefined
        },
      },
      /**
       * The HomepageGallery & HomeFeed keyArgs are set to support the "fetchMore" pagination
       * The cache will only be wiped when those "keyArgs" change
       * */
      homepageGallery: { keyArgs: ['locationId'] },
      homeFeed: { keyArgs: ['locationId', 'cid', 'userId', 'location'] },
      menuPrefetch: { keyArgs: ['locationId', 'categoryId', 'locale'] },
    },
  }

  Object.keys(APOLLO_CACHE_VARIABLES).forEach((item) => {
    /**
     * This typecast is necessary to make sure TS knows that the item is a keyof the list being iterated
     * In this case, there is no way item will be any other string value
     */
    const key = item as keyof typeof APOLLO_CACHE_VARIABLES

    cache.fields = {
      ...cache.fields,
      [key]: {
        read() {
          return APOLLO_CACHE_VARIABLES[key]
        },
      },
    }
  })

  return cache
}

export const localInitialState: Array<{
  query: DocumentNode
  data: Record<string, unknown>
}> = [
  {
    query: GetSearchKeywordDocument,
    data: { searchKeyword: { keyword: null } },
  },
  {
    query: GetSearchCategoryDocument,
    data: { searchCategory: DEFAULT_CATEGORY },
  },
  {
    query: GetSearchLoadingStateDocument,
    data: {
      searchLoadingState: {
        loadingFilters: true,
        loadingResults: true,
      },
    },
  },
  {
    query: GetAuthModalStateDocument,
    data: {
      authModalState: {
        isModalOpen: false,
        modalVariant: null,
        callbackUrl: null,
      },
    },
  },
  {
    query: GetHomeFeedAdsDocument,
    data: {
      homeFeedAds: {
        adSlots: 0,
      },
    },
  },
]

export const writeInitialData = (cache: InMemoryCache, cookies?: RequestHeaders['cookies']) => {
  const initialCookieData = getInitialCookieCacheData(cookies)
  const initialStates = [...localInitialState, ...initialCookieData]

  initialStates.forEach((query) => cache.writeQuery(query))
}

export const initializeCache = (cookies?: RequestHeaders['cookies']): InMemoryCache => {
  const cache = new InMemoryCache({
    typePolicies: {
      Query: {
        fields: getCacheFields().fields,
      },
      ListingLocation: {
        keyFields: false,
      },
    },
  })

  writeInitialData(cache, cookies)
  return cache
}
